import React from 'react'
import {useTranslation} from 'react-i18next'
import styled from 'styled-components'
import {Toggler} from '../../Atoms/Forms'
import {colors} from '../../../sharedComponents/colors'

export type ScheduleFrequency = 'day' | 'week' | 'month' | 'year'

const ToggleWrapper = styled.div`
  label {
    font-size: 0.75rem;
    margin: 0px 0px 1rem;
  }
`

const FrequencySection = styled.fieldset`
  min-width: 0;
  margin: 1.5rem 0 0;
  padding: 1.5rem;
  border: 1px solid ${colors.system.grey_5};
  border-radius: 0.375rem;
`

const FrequencyTitle = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
`

const Description = styled.p`
  margin: 1rem 0 1.5rem;
  color: ${colors.brand.cobalt};
  line-height: 1.5;
`

const FrequencyOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 12rem), 1fr));
  gap: 1rem;
`

const FrequencyOption = styled.label`
  position: relative;
  cursor: pointer;
  input {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
  }
  input:checked + span {
    border-color: ${colors.system.blue};
    background-color: ${colors.system.lightGrey_5};
  }
  input:focus + span {
    outline: 2px solid ${colors.system.blue};
    outline-offset: 2px;
  }
`

const FrequencyCard = styled.span`
  display: flex;
  align-items: center;
  gap: 1rem;
  min-height: 5rem;
  padding: 1rem;
  border: 1px solid ${colors.system.grey_5};
  border-radius: 0.375rem;
  color: ${colors.brand.cobalt};
  font-weight: 600;
  svg {
    flex-shrink: 0;
    color: ${colors.system.blue};
  }
`

const ValidationMessage = styled.p`
  margin: 1rem 0 0;
  color: ${colors.system.red};
  line-height: 1.5;
`

const CalendarIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" aria-hidden="true">
    <rect x="3" y="6" width="22" height="19" rx="2" strokeWidth="1.5" />
    <path d="M8 3v6M20 3v6M3 12h22" strokeWidth="1.5" />
    <path d="M8 16h2m3 0h2m3 0h2M8 20h2m3 0h2m3 0h2" strokeWidth="2" />
  </svg>
)

interface TaskSchedulingProps {
  enabled: boolean
  frequency: ScheduleFrequency | null
  incomplete: boolean
  onToggle: () => void
  onFrequencyChange: (frequency: ScheduleFrequency) => void
}

const TaskScheduling = ({enabled, frequency, incomplete, onToggle, onFrequencyChange}: TaskSchedulingProps) => {
  const {t} = useTranslation('tasks')
  const options: {value: ScheduleFrequency; label: string}[] = [
    {value: 'day', label: t('tasks:labels.schedulingDaily', 'Daily')},
    {value: 'week', label: t('tasks:labels.schedulingWeekly', 'Weekly')},
    {value: 'month', label: t('tasks:labels.schedulingMonthly', 'Monthly')},
    {value: 'year', label: t('tasks:labels.schedulingYearly', 'Yearly')}
  ]

  return (
    <div>
      <ToggleWrapper>
        <Toggler
          label={t('tasks:labels.scheduledTask', 'Scheduled task')}
          values={[t('common:togglerValues.no', 'No'), t('common:togglerValues.yes', 'Yes')]}
          checked={enabled}
          name="isScheduledTask"
          onClick={onToggle}
        />
      </ToggleWrapper>
      {enabled && (
        <FrequencySection
          aria-label={t('tasks:labels.schedulingFrequency', 'Frequency')}
          aria-describedby="schedulingFrequencyDescription"
          aria-invalid={incomplete}
        >
          <FrequencyTitle>{t('tasks:labels.schedulingFrequency', 'Frequency')}</FrequencyTitle>
          <Description id="schedulingFrequencyDescription">
            {t('tasks:description.schedulingFrequency', 'Select how often this task should repeat.')}
          </Description>
          <FrequencyOptions>
            {options.map(option => (
              <FrequencyOption key={option.value}>
                <input
                  type="radio"
                  name="schedulingFrequency"
                  value={option.value}
                  checked={frequency === option.value}
                  onChange={() => onFrequencyChange(option.value)}
                />
                <FrequencyCard>
                  <CalendarIcon />
                  {option.label}
                </FrequencyCard>
              </FrequencyOption>
            ))}
          </FrequencyOptions>
          {incomplete && (
            <ValidationMessage role="alert">
              {t(
                'tasks:validation.schedulingNotAvailable',
                'New or changed schedules cannot be saved yet. Scheduling controls will be available in a later update. Turn scheduling off to save without a schedule, or keep the existing frequency to preserve a saved schedule.'
              )}
            </ValidationMessage>
          )}
        </FrequencySection>
      )}
    </div>
  )
}

export default TaskScheduling
